import React from 'react';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import Button from 'src/components/ui/Button';
import Menu, { MenuItem } from 'src/components/ui/Menu';
import SearchCompanyMembersModal from 'src/components/company/SearchCompanyMembersModal';
import IconButton from 'src/components/ui/IconButton';
import type { CompanyAd, UserAd } from 'src/components/edit-profile/MyPropertyCard';

import { colours } from 'src/constants/colours';
import useAlerts from 'src/hooks/useAlerts';
import useModalOpenState from 'src/hooks/useModalOpenState';

import type { User } from 'src/types/__generated__/graphql';
import { AdStatuses } from 'src/types/__generated__/graphql';
import { useAssignAdsToUserMutation } from 'src/hooks/__generated__/queries';
import { styledComponent } from 'src/utils/styled';
import useBreakpoints from 'src/hooks/useBreakpoints';

const StyledIconButton = styledComponent(IconButton)`
    &.MuiIconButton-root.Mui-disabled {
        background-color: ${colours.grey.G300};
    }
`;
type Ad = CompanyAd | UserAd;

interface Props<T = Ad> {
    ad: T;
    onSoldOrRentedClick: () => void;
    onArchiveClick?: () => void;
    onRestoreClick?: () => void;
    isSelectingAd?: boolean;
}
const MyPropertyCardMenuButton = <T extends Ad>({
    ad,
    onSoldOrRentedClick,
    onArchiveClick,
    onRestoreClick,
    isSelectingAd,
}: Props<T>) => {
    const { t } = useTranslation(['edit-profile', 'common', 'company']);
    const { addAlert } = useAlerts();

    const { props: searchCompanyMembersModalProps, open: openSearchCompanyMembersModal } = useModalOpenState();

    const [assignAdsToUser] = useAssignAdsToUserMutation();

    const handleAssignAdsToUser = async (selectedUser: Pick<User, 'id' | 'fullName'>) => {
        try {
            await assignAdsToUser({
                variables: {
                    userId: selectedUser.id,
                    adIds: [ad.id],
                },
                awaitRefetchQueries: true,
                refetchQueries: ['Ad', 'CompanyAdsSearch', 'Ads'],
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: (error as Error).message,
            });
        }
    };

    const { isMobile, isSmallMobile } = useBreakpoints();

    return (
        <>
            <Menu
                id="more-menu"
                items={[
                    ad.assignedTo && (
                        <MenuItem key="searchCompnayMembers" onClick={() => openSearchCompanyMembersModal()}>
                            <Icon name="PaperClip" size="1.25rem" style={{ marginLeft: '-0.125rem' }} />
                            <Box ml={1}>
                                <Typography variant="h6">{t('edit-profile:modifAssignement')}</Typography>
                            </Box>
                        </MenuItem>
                    ),
                    <MenuItem
                        key="soldOrRented"
                        data-target="my-property-card-menu-button"
                        data-target-id="my-property-card-menu-button-item-soldrented"
                        onClick={onSoldOrRentedClick}
                    >
                        <Icon name="ForSaleDark" size="1.25rem" style={{ marginLeft: '-0.125rem' }} />
                        <Box ml={1}>
                            <Typography variant="h6">
                                {t(`edit-profile:${ad.type === 'sell' ? 'adSold' : 'adRented'}`)}
                            </Typography>
                        </Box>
                    </MenuItem>,
                    ad.status === AdStatuses.Available && (
                        <MenuItem
                            key="archiveAd"
                            data-target="my-property-card-menu-button"
                            data-target-id="my-property-card-menu-button-item-archive"
                            onClick={onArchiveClick}
                        >
                            <Icon name="FileBox" size="1rem" />
                            <Box ml={1}>
                                <Typography variant="h6">{t('edit-profile:archiveAd')}</Typography>
                            </Box>
                        </MenuItem>
                    ),
                    ad.status === AdStatuses.Archived && (
                        <MenuItem
                            key="restoreAd"
                            data-target="my-property-card-menu-button"
                            data-target-id="my-property-card-menu-button-item-restore"
                            onClick={onRestoreClick}
                        >
                            <Icon name="CheckMarkCircleThin" size="1rem" />
                            <Box ml={1}>
                                <Typography variant="h6">{t('edit-profile:restoreMyAd')}</Typography>
                            </Box>
                        </MenuItem>
                    ),
                ]}
            >
                {!isMobile ? (
                    <Box mr="0.5rem" ml="0.5rem">
                        <Button
                            data-target="my-property-card-menu-button"
                            data-target-id="my-property-card-menu-button-more"
                            variant="outlined"
                            color="white"
                            disabled={isSelectingAd}
                            startIcon={<Icon name="DotsHorizontalRounded" />}
                        >
                            {t('common:more')}
                        </Button>
                    </Box>
                ) : (
                    <Box mr="0.5rem">
                        <StyledIconButton
                            data-target="my-property-card-menu-button"
                            data-target-id="my-property-card-menu-button-more"
                            disabled={isSelectingAd}
                            iconProps={{
                                name: 'DotsHorizontalRounded',
                                size: isSmallMobile ? '0.75rem' : '1rem',
                            }}
                        />
                    </Box>
                )}
            </Menu>
            {searchCompanyMembersModalProps.open && ad.owner?.company && (
                <SearchCompanyMembersModal
                    title={t('company:assignCollaborator')}
                    submitLabel={t('company:assign')}
                    variables={{
                        companyId: ad.owner.company.id,
                    }}
                    onSelection={handleAssignAdsToUser}
                    {...searchCompanyMembersModalProps}
                />
            )}
        </>
    );
};

export default MyPropertyCardMenuButton;
